import { useEffect, useState } from "react";
import api, { formatMoney } from "@/lib/api";
import { PageHeader, StatCard, Panel, Pill, Avatar } from "@/components/Bits";
import { useAuth } from "@/contexts/AuthContext";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Baby, Bell, ChalkboardTeacher, CurrencyCircleDollar, ChatCircleText,
  Heart, ForkKnife, Calendar, ChartLineUp, Sparkle, ShoppingBag,
} from "@phosphor-icons/react";

export default function ParentDashboard() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [students, setStudents] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [events, setEvents] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [s, u, i, a, n, e] = await Promise.all([
          api.get("/students"),
          api.get("/daily-updates"),
          api.get("/invoices"),
          api.get("/attendance"),
          api.get("/notifications"),
          api.get("/events"),
        ]);
        setStudents(s.data); setUpdates(u.data);
        setInvoices(i.data); setAttendance(a.data);
        setNotifications(n.data); setEvents(e.data.slice(0, 4));
      } catch (err) { console.error(err); }
    })();
  }, [user]);

  const child = students[0];
  const pendingFees = invoices.reduce((acc, i) => acc + (i.amount_due || 0), 0);
  const last30AttPct = (() => {
    if (!attendance.length) return 0;
    const present = attendance.filter(a => a.status === "present").length;
    return Math.round(present / attendance.length * 100);
  })();
  const recentUpdates = updates.slice(0, 5);

  // Wave R3: role-aware greeting
  const roleGreeting = (() => {
    if (user?.role === "father") return "Hi Dad 🌸";
    if (user?.role === "mother") return "Hi Mum 🌸";
    if (user?.role === "guardian") {
      const gt = user?.guardian_type;
      const map = {
        grandfather: "Hi Grandpa 🌸",
        grandmother: "Hi Grandma 🌸",
        uncle: "Hi Uncle 🌸",
        aunt: "Hi Auntie 🌸",
        elder_sibling: "Hello there! 🌸",
        legal_guardian: "Hello! 🌸",
      };
      return map[gt] || "Hello! 🌸";
    }
    return t("nav.home");
  })();

  return (
    <div>
      <PageHeader
        eyebrow={roleGreeting}
        title={child ? t("parent.welcome", { name: child.name }) + " 🌸" : t("brand")}
        subtitle={t("parent.welcomeShort")}
      />

      {child ? (
        <div className="grid lg:grid-cols-3 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className="relative col-span-1 lg:col-span-2 rounded-3xl overflow-hidden bg-gradient-to-br from-terracotta to-terracotta-hover text-white p-7"
            data-testid="child-hero-card"
          >
            <div className="absolute -bottom-10 -right-10 w-64 h-64 rounded-full bg-mustard/30 blur-3xl" />
            <div className="absolute top-6 right-6 text-6xl opacity-70 animate-float">🌈</div>
            <div className="text-xs uppercase tracking-[0.18em] opacity-80">{t("common.today")}</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mt-2 leading-tight">{child.name}</h2>
            <div className="mt-2 text-white/85">{child.age_group} · {child.class_name}</div>
            <div className="mt-6 grid grid-cols-3 gap-3 max-w-md">
              <Stat label={t("parent.attendance")} value={`${last30AttPct}%`} />
              <Stat label={t("parent.moodToday")} value={updates[0]?.mood || "—"} />
              <Stat label={t("parent.updates")} value={updates.length} />
            </div>
            <div className="mt-6 flex flex-wrap gap-2">
              <Link to="/app/timeline" className="px-4 py-2 rounded-full bg-white text-terracotta text-sm font-semibold" data-testid="parent-view-timeline">{t("parent.viewTimeline")}</Link>
              <Link to="/app/parent-fees" className="px-4 py-2 rounded-full bg-white/15 text-white text-sm font-semibold border border-white/20" data-testid="parent-pay-fees">{t("parent.payFees")}</Link>
            </div>
          </motion.div>

          <Panel title={t("dashboard.todayActions")} testid="quick-actions-panel">
            <div className="grid grid-cols-2 gap-3">
              {[
                { to: "/app/timeline",          labelKey: "nav.dailyTimeline", icon: ChartLineUp,        tone: "bg-terracotta-soft text-terracotta" },
                { to: "/app/parent-attendance", labelKey: "nav.attendance",    icon: ChalkboardTeacher,  tone: "bg-sage-soft text-sage-deep" },
                { to: "/app/parent-fees",       labelKey: "nav.fees",          icon: CurrencyCircleDollar, tone: "bg-mustard-soft text-mustard-hover" },
                { to: "/app/parent-apparel",    labelKey: "nav.apparel",       icon: ShoppingBag,        tone: "bg-lavender-soft text-lavender-deep" },
                { to: "/app/parent-meals",      labelKey: "nav.meals",         icon: ForkKnife,          tone: "bg-mustard-soft text-mustard-hover" },
                { to: "/app/parent-events",     labelKey: "parent.events",     icon: Calendar,           tone: "bg-terracotta-soft text-terracotta" },
              ].map((q) => (
                <Link key={q.to} to={q.to} className="p-3 rounded-2xl border border-border hover:bg-paper-alt transition" data-testid={`qa-${q.to.split("/").pop()}`}>
                  <span className={`w-9 h-9 rounded-xl ${q.tone} flex items-center justify-center mb-2`}>
                    <q.icon weight="duotone" />
                  </span>
                  <div className="text-sm font-semibold">{t(q.labelKey)}</div>
                </Link>
              ))}
            </div>
          </Panel>
        </div>
      ) : (
        <Panel>
          <div className="text-center py-10">
            <Baby size={48} weight="duotone" className="mx-auto text-terracotta" />
            <h3 className="font-heading font-bold text-xl mt-3">{t("parent.noChildSelected")}</h3>
            <p className="text-ink-soft mt-2">{t("empty.noStudentsHint", { defaultValue: "Once your admission is approved, your child's profile will appear here." })}</p>
          </div>
        </Panel>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <Panel title={t("parent.todayBites")} className="lg:col-span-1" testid="panel-todays-bites">
          {recentUpdates.length === 0 ? (
            <div className="text-center py-6 text-ink-soft text-sm">{t("common.allCaughtUp")}</div>
          ) : (
            <div className="space-y-3">
              {recentUpdates.map((u) => (
                <motion.div key={u.update_id} initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} className="p-4 rounded-2xl bg-paper-alt">
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-ink-soft">{u.date}</div>
                    <Pill tone="mustard">{u.mood}</Pill>
                  </div>
                  {u.activities?.[0] && <div className="text-sm mt-2 font-medium">• {u.activities[0]}</div>}
                  {u.activities?.[1] && <div className="text-sm mt-1 text-ink-soft">• {u.activities[1]}</div>}
                </motion.div>
              ))}
            </div>
          )}
        </Panel>

        <Panel title={t("parent.notices")} className="lg:col-span-1" testid="panel-notices">
          {notifications.length === 0 ? (
            <div className="text-center py-6 text-ink-soft text-sm">{t("common.allCaughtUp")} 🌷</div>
          ) : (
            <div className="space-y-2">
              {notifications.slice(0, 6).map((n) => (
                <div key={n.notification_id} className="p-3 rounded-2xl hover:bg-paper-alt">
                  <div className="flex items-center justify-between">
                    <div className="font-semibold text-sm">{n.title}</div>
                    {!n.read && <span className="w-2 h-2 rounded-full bg-terracotta" />}
                  </div>
                  <div className="text-xs text-ink-soft mt-1">{n.body}</div>
                </div>
              ))}
            </div>
          )}
        </Panel>

        <Panel title={t("parent.upcoming")} className="lg:col-span-1">
          <div className="space-y-2">
            {events.map((ev) => (
              <Link to="/app/parent-events" key={ev.event_id} className="block p-3 rounded-2xl border border-border hover:bg-paper-alt">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-mustard-soft text-mustard-hover flex items-center justify-center font-bold text-xs">
                    {new Date(ev.event_date).getDate()}
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-sm">{ev.name}</div>
                    <div className="text-xs text-ink-soft">{ev.event_date}</div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </Panel>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-2xl bg-white/15 backdrop-blur-sm border border-white/15 p-3">
      <div className="text-xs uppercase tracking-wider opacity-80">{label}</div>
      <div className="font-heading text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}
